import { env } from '@/config';
import { STORAGE_KEYS } from '@/constants/storage-keys.constants';
import {
  localStorageAdapter,
  memoryStorage,
  resolveStorageAdapter,
  sessionStorageAdapter,
  type IStorageAdapter,
} from './storage.util';

const defaultAdapter = resolveStorageAdapter(env.auth.tokenStorageStrategy);
const persistentAdapters = [localStorageAdapter, sessionStorageAdapter, memoryStorage];

// rememberMe === true persists across browser restarts, false clears when the tab closes,
// undefined preserves whichever adapter already holds the key (e.g. during a token refresh).
function adapterFor(key: string, rememberMe?: boolean): IStorageAdapter {
  if (rememberMe === true) return localStorageAdapter;
  if (rememberMe === false) return sessionStorageAdapter;

  return persistentAdapters.find((candidate) => candidate.getItem(key) !== null) ?? defaultAdapter;
}

function readAny(key: string): string | null {
  for (const adapter of persistentAdapters) {
    const value = adapter.getItem(key);
    if (value !== null) return value;
  }
  return null;
}

function removeFromAll(key: string): void {
  persistentAdapters.forEach((adapter) => adapter.removeItem(key));
}

function write(key: string, value: string, rememberMe?: boolean): void {
  const adapter = adapterFor(key, rememberMe);
  removeFromAll(key);
  adapter.setItem(key, value);
}

export const tokenStorage = {
  getAccessToken(): string | null {
    return readAny(STORAGE_KEYS.ACCESS_TOKEN);
  },
  setAccessToken(token: string, rememberMe?: boolean): void {
    write(STORAGE_KEYS.ACCESS_TOKEN, token, rememberMe);
  },
  removeAccessToken(): void {
    removeFromAll(STORAGE_KEYS.ACCESS_TOKEN);
  },
  getRefreshToken(): string | null {
    return readAny(STORAGE_KEYS.REFRESH_TOKEN);
  },
  setRefreshToken(token: string, rememberMe?: boolean): void {
    write(STORAGE_KEYS.REFRESH_TOKEN, token, rememberMe);
  },
  removeRefreshToken(): void {
    removeFromAll(STORAGE_KEYS.REFRESH_TOKEN);
  },
  clearAll(): void {
    removeFromAll(STORAGE_KEYS.ACCESS_TOKEN);
    removeFromAll(STORAGE_KEYS.REFRESH_TOKEN);
  },
};
